/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState, useCallback, ClipboardEvent, SyntheticEvent } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { EditorState, ContentState, convertToRaw, AtomicBlockUtils, Modifier } from 'draft-js';
import { Editor } from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import { API } from '../../../../../../../api';
import { blobToFile } from '../../../../../components/common';
import { reactDraftWysiwygToolbarOptionstask } from '../../../../../../../utils/react-draft-wysiwyg-options';
import imageCompression from 'browser-image-compression';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import _ from 'lodash';
import { toast } from 'react-toastify';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {
  getMediaDraftChunk,
  customEntityTransform,
  normalizeImageAlignment,
  ImageAlignment
} from './lessonDescriptionVideoHtml';
import { safeContentStateFromHtml } from '../../../../../../../utils/draftHtml';
import htmlToDraft from 'html-to-draftjs';

const acceptedImages = 'image/jpeg,image/jpg,image/png';

export interface LessonDescriptionEditorInsertRef {
  insertImage: (url: string) => void;
  insertVideo: (url: string) => void;
}

interface LessonDescriptionEditorProps {
  value?: string;
  onChange: (html: string) => void;
  onBlur?: (event: SyntheticEvent<any>) => void;
  placeholder?: string;
  editorClassName?: string;
  wrapperClassName?: string;
  toolbarClassName?: string;
  insertMediaRef?: React.MutableRefObject<LessonDescriptionEditorInsertRef | null>;
}

export const ImageBlock = ({
  block,
  contentState,
  blockProps
}: {
  block: any;
  contentState: ContentState;
  blockProps: any;
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 400, height: 'auto' });
  // Mirror dimensions in a ref: the window mousemove/mouseup listeners are bound
  // once (in the isResizing effect) and otherwise close over the pre-drag
  // dimensions, so saveImageDimensions must read the latest size from here —
  // otherwise it persists the size from before the drag and the image reverts.
  const dimensionsRef = useRef(dimensions);
  const resizeRef = useRef<HTMLDivElement>(null);
  // Measured separately from `resizeRef`: the outer `image-block` is a full-width
  // flex container (so alignment works), so its rect width is the editor width,
  // not the image's. The aspect ratio must come from the <img> itself.
  const imageRef = useRef<HTMLImageElement>(null);
  const startResizeRef = useRef({ x: 0, y: 0, width: 0, height: 0 });
  const hasResized = useRef(false);
  const entityKey = block.getEntityAt(0);
  const { setEditorState, descriptionkey, onImageResize, setDescription } = blockProps.blockProps;
  const entity = entityKey !== null ? contentState.getEntity(entityKey) : undefined;

  useEffect(() => {
    if (entity && entity.getType() === 'IMAGE') {
      const { width, height } = entity.getData();
      const numericWidth = typeof width === 'string' ? parseInt(width.replace('px', ''), 10) : width || 400;
      const numericHeight = typeof height === 'string' ? parseInt(height.replace('px', ''), 10) : height || 'auto';
      const next = { width: numericWidth, height: numericHeight };
      dimensionsRef.current = next;
      setDimensions(next);
    }
  }, [entity]);

  useEffect(() => {
    const handleBlur = (e: MouseEvent) => {
      if (hasResized.current && entity && entityKey && !resizeRef.current?.contains(e.target as Node)) {
        void saveImageDimensions();
        hasResized.current = false;
      }
    };

    document.addEventListener('mousedown', handleBlur);
    return () => {
      document.removeEventListener('mousedown', handleBlur);
      if (hasResized.current) {
        void saveImageDimensions();
      }
    };
  }, [dimensions, entity, entityKey]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    // Measure the image element (not the full-width flex wrapper) so the
    // aspect ratio used to keep proportion on resize is the image's real ratio.
    const measured = imageRef.current ?? resizeRef.current;
    if (measured) {
      const rect = measured.getBoundingClientRect();
      startResizeRef.current = {
        x: e.clientX,
        y: e.clientY,
        width: rect.width,
        height: rect.height
      };
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - startResizeRef.current.x;
    const newWidth = Math.max(100, startResizeRef.current.width + deltaX);
    const aspectRatio = startResizeRef.current.height / startResizeRef.current.width;
    const newHeight: any =
      Number.isFinite(aspectRatio) && aspectRatio > 0 ? Math.round(newWidth * aspectRatio) : 'auto';

    const next = { width: newWidth, height: newHeight };
    dimensionsRef.current = next;
    setDimensions(next);
    hasResized.current = true;
  };

  const handleMouseUp = () => {
    if (!isResizing) return;
    setIsResizing(false);
    if (hasResized.current) {
      void saveImageDimensions();
      hasResized.current = false;
    }
  };

  useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const saveImageDimensions = async () => {
    if (entity && entityKey) {
      const currentDimensions = dimensionsRef.current;
      const numericWidth = parseInt(currentDimensions.width.toString(), 10);
      const numericHeight =
        currentDimensions.height === 'auto' ? 'auto' : parseInt(currentDimensions.height.toString(), 10);

      const newData = {
        ...entity.getData(),
        width: numericWidth,
        height: numericHeight
      };

      const newContentState = contentState.replaceEntityData(entityKey, newData);
      const newEditorState = EditorState.push(blockProps.blockProps.editorState, newContentState, 'apply-entity');

      setEditorState(newEditorState);

      if (onImageResize) {
        onImageResize(newContentState);
      }

      // Serialize through the same draftjs-to-html path the rest of the editor
      // uses (insert/save). buildImageHtmlFromEntity preserves the author's
      // alignment instead of forcing the resized image to centre.
      const htmlContent = draftToHtml(convertToRaw(newContentState), undefined, undefined, customEntityTransform);

      if (setDescription) {
        setDescription(htmlContent);
      }
    }
  };

  const currentAlignment: ImageAlignment = normalizeImageAlignment(entity?.getData()?.alignment);

  const applyAlignment = (alignment: ImageAlignment) => {
    if (!entity || !entityKey) return;
    const newData = { ...entity.getData(), alignment };
    const newContentState = contentState.replaceEntityData(entityKey, newData);
    const newEditorState = EditorState.push(blockProps.blockProps.editorState, newContentState, 'apply-entity');

    setEditorState(newEditorState);

    if (onImageResize) {
      onImageResize(newContentState);
    }

    const htmlContent = draftToHtml(convertToRaw(newContentState), undefined, undefined, customEntityTransform);
    if (setDescription) {
      setDescription(htmlContent);
    }
  };

  const removeBlock = async () => {
    if (entity) {
      const imageData = entity.getData();
      const entityType = entity.getType();

      if (entityType === 'IMAGE') {
        const blobUrl = imageData.src;
        const fileName = decodeURIComponent(blobUrl.split('/').pop().split('?')[0]);

        try {
          const deletedFiles = JSON.parse(localStorage.getItem('deletedFiles') || '[]');
          localStorage.setItem('deletedFiles', JSON.stringify([...deletedFiles, { fileName, entityKey }]));
          await removeBlockFromEditor();
        } catch (error) {
          setError('Failed to delete image');
        }
      } else {
        await removeBlockFromEditor();
      }
    }
  };

  const removeBlockFromEditor = async () => {
    const { editorState } = blockProps.blockProps;
    const selectionState = editorState.getSelection();
    const blockKey = block.getKey();

    const blockSelection = selectionState.merge({
      anchorKey: blockKey,
      focusKey: blockKey,
      anchorOffset: 0,
      focusOffset: block.getLength()
    });

    const contentStateWithoutBlock = Modifier.removeRange(contentState, blockSelection, 'backward');
    const newBlockMap = contentStateWithoutBlock.getBlockMap().toSeq();
    const nextBlockKey = contentStateWithoutBlock.getKeyAfter(blockKey);
    const nextSelection = selectionState.merge({
      anchorKey: nextBlockKey || newBlockMap.first().getKey(),
      focusKey: nextBlockKey || newBlockMap.first().getKey(),
      anchorOffset: 0,
      focusOffset: 0
    });

    const newEditorState = EditorState.push(editorState, contentStateWithoutBlock, 'remove-range');
    setEditorState(EditorState.forceSelection(newEditorState, nextSelection));
  };

  return (
    <Box
      className="image-block"
      ref={resizeRef}
      sx={
        entity?.getType() === 'IMAGE'
          ? {
              display: 'flex',
              justifyContent:
                currentAlignment === 'center'
                  ? 'center'
                  : currentAlignment === 'right'
                  ? 'flex-end'
                  : 'flex-start'
            }
          : undefined
      }
    >
      {entityKey !== null && entity && (
        <>
          {entity.getType() === 'IMAGE' ? (
            <Box className="image-container" sx={{ position: 'relative', display: 'inline-block' }}>
              <Box
                className="image-align-toolbar"
                contentEditable={false}
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  display: 'flex',
                  gap: '2px',
                  p: '2px',
                  borderRadius: '6px',
                  background: 'rgba(0, 0, 0, 0.6)',
                  zIndex: 3
                }}
              >
                {(['left', 'center', 'right'] as ImageAlignment[]).map((align) => (
                  <Box
                    component="button"
                    type="button"
                    key={align}
                    aria-label={`Align image ${align}`}
                    onMouseDown={(e: React.MouseEvent) => {
                      // Keep the click from triggering resize/selection.
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      applyAlignment(align);
                    }}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      cursor: 'pointer',
                      p: '2px',
                      borderRadius: '4px',
                      lineHeight: 0,
                      color: '#fff',
                      background: currentAlignment === align ? '#F15A26' : 'transparent'
                    }}
                  >
                    {align === 'left' ? (
                      <FormatAlignLeftIcon sx={{ fontSize: 16 }} />
                    ) : align === 'center' ? (
                      <FormatAlignCenterIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <FormatAlignRightIcon sx={{ fontSize: 16 }} />
                    )}
                  </Box>
                ))}
              </Box>
              <Box
                component="img"
                ref={imageRef}
                src={entity.getData().src}
                alt="Upload attachment file"
                className="resizable-image"
                sx={{
                  width: `${dimensions.width}px`,
                  height: dimensions.height === 'auto' ? 'auto' : `${dimensions.height}px`
                }}
              />
              <Box
                className="resize-handle"
                onMouseDown={handleMouseDown}
                sx={{
                  position: 'absolute',
                  right: '0',
                  bottom: '0',
                  width: '20px',
                  height: '20px',
                  cursor: 'se-resize',
                  background: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '50%'
                }}
              />
              <button onClick={removeBlock} className="delete-button" aria-label="Delete image">
                ×
              </button>
            </Box>
          ) : entity.getType() === 'VIDEO' ? (
            <Box className="image-container">
              <video
                controls
                style={{
                  width: '100%',
                  maxWidth: 560,
                  borderRadius: 4,
                  background: '#000'
                }}
              >
                <source src={entity.getData().src} />
              </video>
              <button onClick={removeBlock} className="delete-button" aria-label="Delete video">
                ×
              </button>
            </Box>
          ) : (
            <Box className="iframe-container">
              <iframe
                src={entity.getData().src}
                title="Inserted content"
                className="embedded-iframe"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                frameBorder="0"
              />
              <button onClick={removeBlock} className="delete-button" aria-label="Delete iframe">
                ×
              </button>
            </Box>
          )}

          {error && (
            <Typography variant="h6" color="error">
              {error}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

const ensurePx = (value: any) => (typeof value === 'number' ? `${value}px` : value.endsWith('px') ? value : `${value}px`);

const sanitizeValue = (htmlValue: string) => htmlValue.replace(/autopx/g, 'auto').replace(/<p><\/p>$/, '');

// Pasted HTML (Word / Google Docs / web pages) carries its own inline font size,
// family and colour, which made pasted copy render inconsistently in the editor.
// Strip that styling while keeping semantic structure (bold, italics, lists,
// links) so pasted text always adopts the editor's own size and style.
const cleanPastedHtml = (html: string): string => {
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    // Drop foreign inline styling / presentational attributes from every element.
    doc.querySelectorAll('*').forEach((el) => {
      el.removeAttribute('style');
      el.removeAttribute('class');
      el.removeAttribute('color');
      el.removeAttribute('face');
      el.removeAttribute('size');
      el.removeAttribute('align');
    });
    // Headings would import as large header blocks - normalise to paragraphs so
    // pasted text keeps the field's size (emphasis is preserved via <strong>).
    doc.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((h) => {
      const p = doc.createElement('p');
      const strong = doc.createElement('strong');
      strong.innerHTML = h.innerHTML;
      p.appendChild(strong);
      h.replaceWith(p);
    });
    // Unwrap span / font wrappers - their only purpose here was to carry styling.
    doc.querySelectorAll('span,font').forEach((el) => {
      const parent = el.parentNode;
      if (!parent) return;
      while (el.firstChild) parent.insertBefore(el.firstChild, el);
      parent.removeChild(el);
    });
    return doc.body.innerHTML;
  } catch {
    return html;
  }
};

const LessonDescriptionEditor: React.FC<LessonDescriptionEditorProps> = ({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Add Lesson Body here',
  editorClassName = 'taskEditorClass',
  wrapperClassName = 'wrapperClassName p-0 b-0',
  toolbarClassName = 'toolBarStyle toolBarStyleTask',
  insertMediaRef
}) => {
  // Build the editor from `value` on the first render, not in an effect. The
  // toolbar's hidden font-family control (CustomFontFamilyDropdown) calls
  // onChange from its own mount effect, which runs before any effect here. If
  // the editor starts empty, that call reports "" as an edit and the parent's
  // copy is wiped before it can load, so the editor always opened blank.
  // Starting with the content means that call only ever sees the real lesson.
  const [editorState, setEditorState] = useState(() =>
    EditorState.createWithContent(safeContentStateFromHtml(sanitizeValue(value || ''), getMediaDraftChunk))
  );
  const valueRef = useRef(sanitizeValue(value || ''));
  const lastValuePropRef = useRef(value);

  const updateDescription = useCallback(
    (html: string) => {
      const sanitized = sanitizeValue(html);
      valueRef.current = sanitized;
      onChange(sanitized);
    },
    [onChange]
  );

  const insertImage = (currentEditorState: EditorState, imageUrl: string) => {
    const contentState = currentEditorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', { src: imageUrl, alt: 'image' });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    return AtomicBlockUtils.insertAtomicBlock(currentEditorState, entityKey, ' ');
  };

  const uploadTaskDescriptionFile = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    // Let the real HTTP error propagate: both callers (imageUpload and
    // handleVideoUpload) wrap this in try/catch and surface the failure. Swallowing
    // it here previously turned every upload error into a silent no-op.
    const result = await API.saveTaskDescriptionFiles(formData);
    const blobUrl = _.get(_.head(_.get(result, 'data.data')), 'Bloburl', '');
    return { data: { link: blobUrl } };
  };

  const handleImageCompression = async (imageFile: any) => {
    const options = {
      maxSizeMB: 1,
      useWebWorker: true
    };
    try {
      const compressedFile = await imageCompression(imageFile, options);
      if (compressedFile) {
        return blobToFile(compressedFile, imageFile?.name, imageFile?.type);
      }
    } catch (error) {
      console.error('Image compression failed', error);
    }
    return null;
  };

  const embedCallback = (url: any) => {
    if (url.indexOf('youtu.be/') >= 0) {
      const videoId = url.match(/youtu\.be\/([^?]+)/);
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId[1]}`;
      }
    } else if (url.indexOf('youtube') >= 0) {
      url = url.replace('watch?v=', 'embed/');
      url = url.replace('/watch/', '/embed/');
      url = url.replace('youtu.be/', 'youtube.com/embed/');
    }
    return url;
  };

  const imageUpload = async (file: any) => {
    if (!acceptedImages.includes(file.type)) {
      return { error: 'Image upload failed' };
    }
    try {
      let uploadFile: File | null = file;
      if (file.size > 1024 * 1024) {
        uploadFile = await handleImageCompression(file);
      }
      if (uploadFile) {
        const result = await uploadTaskDescriptionFile(uploadFile);
        return result;
      }
    } catch (error) {
      return { error: 'Image upload failed' };
    }
    return { error: 'Image upload failed' };
  };

  const extendedToolbarOptions = reactDraftWysiwygToolbarOptionstask(imageUpload, embedCallback);

  const [videoUploading, setVideoUploading] = useState(false);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;

  useEffect(() => {
    if (!insertMediaRef) return;
    insertMediaRef.current = {
      insertImage: (url: string) => {
        const latest = editorStateRef.current;
        const contentState = latest.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', { src: url, alt: 'image' });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = AtomicBlockUtils.insertAtomicBlock(latest, entityKey, ' ');
        setEditorState(newEditorState);
        const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()), undefined, undefined, customEntityTransform);
        updateDescription(sanitizeValue(html));
      },
      insertVideo: (url: string) => {
        const latest = editorStateRef.current;
        const contentState = latest.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity('VIDEO', 'IMMUTABLE', { src: url });
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
        const newEditorState = AtomicBlockUtils.insertAtomicBlock(latest, entityKey, ' ');
        setEditorState(newEditorState);
        const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()), undefined, undefined, customEntityTransform);
        updateDescription(sanitizeValue(html));
      }
    };
  });

  const handleVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error('Please choose a video file (MP4, WebM or OGG).');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error(
        `This video is ${Math.round(
          file.size / (1024 * 1024)
        )} MB. The maximum size is 100 MB. Compress it and try again.`
      );
      return;
    }

    setVideoUploading(true);
    try {
      const result = await uploadTaskDescriptionFile(file);
      const url = (result as any)?.data?.link;
      if (!url) {
        toast.error('The video could not be uploaded. Try again.');
        return;
      }
      const latestEditorState = editorStateRef.current;
      const contentState = latestEditorState.getCurrentContent();
      const contentStateWithEntity = contentState.createEntity('VIDEO', 'IMMUTABLE', { src: url });
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = AtomicBlockUtils.insertAtomicBlock(latestEditorState, entityKey, ' ');
      setEditorState(newEditorState);
      const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()), undefined, undefined, customEntityTransform);
      updateDescription(sanitizeValue(html));
    } catch (error) {
      console.error('Lesson video upload failed', error);
      toast.error('The video could not be uploaded. Try again.');
    } finally {
      setVideoUploading(false);
    }
  };

  const handleEditorStateChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    let contentState = newEditorState.getCurrentContent();
    contentState.getBlockMap().forEach((block) => {
      if (block) {
        const entityKey = block.getEntityAt(0);
        if (entityKey) {
          const entity = contentState.getEntity(entityKey);
          if (entity.getType() === 'IMAGE') {
            const data = entity.getData();
            const width = ensurePx(data.width || 'auto');
            const height = ensurePx(data.height || 'auto');
            const updatedData = { ...data, width, height };
            contentState = contentState.replaceEntityData(entityKey, updatedData);
          }
        }
      }
    });

    const hasContent =
      contentState.hasText() ||
      contentState.getBlockMap().some((block) => (block ? block.getType() !== 'unstyled' : false));

    const html = hasContent ? draftToHtml(convertToRaw(contentState), undefined, undefined, customEntityTransform) : '';
    const sanitizedHtml = sanitizeValue(html);
    if (valueRef.current !== sanitizedHtml) {
      updateDescription(sanitizedHtml);
    }
  };

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.indexOf('image') === 0) {
          const file = item.getAsFile();
          if (file) {
            imageUpload(file)
              .then((response: any) => {
                if (response?.data?.link) {
                  const imageUrl = response.data.link;
                  const newEditorState = insertImage(editorState, imageUrl);
                  setEditorState(newEditorState);
                  event.preventDefault();
                }
              })
              .catch((error: any) => {
                console.error('Image upload error:', error);
              });
          }
        }
      }
    },
    [editorState]
  );

  useEffect(() => {
    const editorContainer = document.querySelector(`.${editorClassName}`);
    if (editorContainer) {
      editorContainer.addEventListener('paste', handlePaste as any);
    }
    return () => {
      if (editorContainer) {
        editorContainer.removeEventListener('paste', handlePaste as any);
      }
    };
  }, [handlePaste, editorClassName]);

  // Re-load only when the `value` prop itself changes after mount (e.g. an
  // external "Improve with AI" rewrite); the first render already loaded it.
  // Comparing against the last prop, not just valueRef, matters on mount: the
  // toolbar's onChange can update valueRef first, and this effect would then
  // re-load the same copy with the cursor forced to the end.
  useEffect(() => {
    if (value === lastValuePropRef.current) {
      return;
    }
    lastValuePropRef.current = value;
    const sanitized = sanitizeValue(value || '');
    if (sanitized === valueRef.current) {
      return;
    }
    const contentState = safeContentStateFromHtml(sanitized, getMediaDraftChunk);
    // Jump to the end so replaced copy is in view. The initial load above
    // leaves the cursor at the start, so the editor doesn't open scrolled down.
    setEditorState(EditorState.moveFocusToEnd(EditorState.createWithContent(contentState)));
    valueRef.current = sanitized;
  }, [value]);

  const blockRender = (contentBlock: any) => {
    const contentState = editorState.getCurrentContent();
    if (contentBlock.getType() === 'atomic' || contentBlock.getText() === '📷') {
      return {
        component: ImageBlock,
        editable: false,
        props: {
          contentState,
          blockProps: {
            editorState,
            setEditorState,
            descriptionkey: editorClassName,
            onImageResize: (newContentState: ContentState) => {
              const newEditorState = EditorState.push(editorState, newContentState, 'apply-entity');
              setEditorState(newEditorState);
              const html = draftToHtml(convertToRaw(newContentState), undefined, undefined, customEntityTransform);
              updateDescription(html);
            },
            setDescription: updateDescription
          }
        }
      };
    }
    return null;
  };

  const videoUploadBtn = (
    <Box
      key="video-upload"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        cursor: videoUploading ? 'wait' : 'pointer',
        opacity: videoUploading ? 0.5 : 1,
        px: 0.5,
      }}
      title="Upload video (mp4, webm, ogg, max 100MB)"
      onClick={(e) => {
        e.stopPropagation();
        if (!videoUploading) videoInputRef.current?.click();
      }}
    >
      {/* No spinner in the button: the "Uploading video" panel below shows progress. */}
      <VideocamOutlinedIcon sx={{ fontSize: 20, color: '#868686' }} />
    </Box>
  );

  return (
    <Box>
      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/webm,video/ogg"
        style={{ display: 'none' }}
        onChange={handleVideoUpload}
      />
      <Editor
        editorState={editorState}
        onEditorStateChange={handleEditorStateChange}
        handlePastedText={(_text: string, html: string) => {
          // Only intercept rich (HTML) pastes; plain text already adopts the
          // field's style. Clean out foreign size/font/colour, keep structure,
          // then insert as a fragment at the cursor.
          if (!html) return false;
          const cleaned = cleanPastedHtml(html);
          if (!cleaned.trim()) return false;
          // htmlToDraft yields no blocks for markup it can't parse (tables,
          // iframes, unknown embeds). Hand those back to Draft's default paste
          // instead of replacing the selection with nothing: the safe wrapper's
          // empty-block fallback always has size 1, so its block map can't tell
          // "parsed" from "failed". Same for markup that parses to empty text.
          const parsed = htmlToDraft(cleaned, getMediaDraftChunk);
          const blocks = parsed?.contentBlocks;
          if (!Array.isArray(blocks) || blocks.length === 0) return false;
          if (!blocks.some((b) => b.getText().trim().length > 0 || b.getType() === 'atomic')) return false;
          const fragment = ContentState.createFromBlockArray(blocks, parsed.entityMap).getBlockMap();
          const current = editorStateRef.current;
          const newContent = Modifier.replaceWithFragment(
            current.getCurrentContent(),
            current.getSelection(),
            fragment
          );
          handleEditorStateChange(EditorState.push(current, newContent, 'insert-fragment'));
          return true;
        }}
        toolbar={extendedToolbarOptions}
        toolbarCustomButtons={[videoUploadBtn]}
        placeholder={placeholder}
        wrapperClassName={wrapperClassName}
        editorClassName={`${editorClassName} taskEditorClass`}
        toolbarClassName={toolbarClassName}
        onBlur={(event) => onBlur?.(event)}
        customBlockRenderFunc={blockRender}
      />
      {videoUploading && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 1.5,
            mt: 1,
            bgcolor: 'rgba(0,0,0,0.4)',
            borderRadius: '6px',
            border: '1px solid #868686',
          }}
        >
          <CircularProgress size={20} sx={{ color: '#F79420' }} />
          <Typography sx={{ color: '#FDFDFD', fontSize: 13 }}>Uploading video…</Typography>
        </Box>
      )}
    </Box>
  );
};

export default LessonDescriptionEditor;
